import React from 'react';
import {
  Box, VStack, HStack, Heading, Text, Button, SimpleGrid, Card, CardBody,
  Icon, Badge, Divider, List, ListItem, ListIcon, Flex,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { MdAssignment, MdUploadFile, MdSearch, MdVerifiedUser, MdAccessTime, MdPlace } from 'react-icons/md';
import { CheckCircleIcon } from '@chakra-ui/icons';

const PortalObrasHome: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Box minH="100vh" bg="gray.50">
      {/* Header */}
      <Box bg="#5b182b" py={8} px={{ base: 4, md: 10 }} color="white" textAlign="center">
        <Flex justify="center" align="center" mb={2}>
          <Box bg="white" borderRadius="full" w={14} h={14} display="flex" alignItems="center" justifyContent="center" mr={4}>
            <Icon as={MdPlace} color="#5b182b" boxSize={8} />
          </Box>
          <Box textAlign="left">
            <Heading size="lg">Portal Ciudadano</Heading>
            <Text fontSize="sm" opacity={0.85}>Dirección de Obras Públicas y Desarrollo Urbano</Text>
          </Box>
        </Flex>
        <Text fontSize="sm" opacity={0.75} mt={2}>H. Ayuntamiento de Totolac · Tlaxcala</Text>
      </Box>

      <Box py={10} px={{ base: 4, md: 10 }} maxW="1000px" mx="auto">
        <VStack spacing={8}>
          <Box textAlign="center">
            <Heading size="md" color="gray.700">¿Qué desea realizar?</Heading>
            <Text color="gray.500" mt={1} fontSize="sm">Seleccione la opción que corresponda a su necesidad.</Text>
          </Box>

          {/* Opciones principales */}
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5} w="full">
            {/* Opción A: Pre-registro presencial */}
            <Card shadow="md" _hover={{ shadow: 'lg', transform: 'translateY(-2px)' }} transition="all 0.2s" cursor="pointer"
              onClick={() => navigate('/portal/obras/pre-registro')}>
              <CardBody p={6}>
                <HStack spacing={4} mb={4}>
                  <Box bg="orange.100" borderRadius="full" p={3}>
                    <Icon as={MdAssignment} color="orange.500" boxSize={7} />
                  </Box>
                  <Box>
                    <Heading size="sm" color="orange.600">Opción A</Heading>
                    <Text fontWeight="bold" color="gray.700">Trámite Presencial</Text>
                  </Box>
                </HStack>
                <Text fontSize="sm" color="gray.600" mb={4}>
                  Registre sus datos por anticipado para agilizar su atención en las oficinas del Ayuntamiento.
                </Text>
                <List spacing={1} mb={4}>
                  <ListItem fontSize="xs" color="gray.500"><ListIcon as={CheckCircleIcon} color="orange.400" />Pre-registro en línea</ListItem>
                  <ListItem fontSize="xs" color="gray.500"><ListIcon as={CheckCircleIcon} color="orange.400" />Folio de seguimiento inmediato</ListItem>
                  <ListItem fontSize="xs" color="gray.500"><ListIcon as={CheckCircleIcon} color="orange.400" />Presenta documentos en ventanilla</ListItem>
                </List>
                <Button colorScheme="orange" size="sm" w="full" onClick={() => navigate('/portal/obras/pre-registro')}>
                  Iniciar Pre-registro →
                </Button>
              </CardBody>
            </Card>

            {/* Opción B: Trámite digital */}
            <Card shadow="md" _hover={{ shadow: 'lg', transform: 'translateY(-2px)' }} transition="all 0.2s" cursor="pointer"
              onClick={() => navigate('/portal/obras/tramite-digital')}>
              <CardBody p={6}>
                <HStack spacing={4} mb={4}>
                  <Box bg="blue.100" borderRadius="full" p={3}>
                    <Icon as={MdUploadFile} color="blue.500" boxSize={7} />
                  </Box>
                  <Box>
                    <Heading size="sm" color="blue.600">Opción B</Heading>
                    <Text fontWeight="bold" color="gray.700">Trámite Digital</Text>
                  </Box>
                </HStack>
                <Text fontSize="sm" color="gray.600" mb={4}>
                  Inicie y gestione su trámite completamente en línea, cargando sus documentos desde cualquier dispositivo.
                </Text>
                <List spacing={1} mb={4}>
                  <ListItem fontSize="xs" color="gray.500"><ListIcon as={CheckCircleIcon} color="blue.400" />Captura de datos en línea</ListItem>
                  <ListItem fontSize="xs" color="gray.500"><ListIcon as={CheckCircleIcon} color="blue.400" />Carga de documentos digitales</ListItem>
                  <ListItem fontSize="xs" color="gray.500"><ListIcon as={CheckCircleIcon} color="blue.400" />Seguimiento desde el portal</ListItem>
                </List>
                <Button colorScheme="blue" size="sm" w="full" onClick={() => navigate('/portal/obras/tramite-digital')}>
                  Iniciar Trámite Digital →
                </Button>
              </CardBody>
            </Card>
          </SimpleGrid>

          <Divider />

          {/* Consulta y validación */}
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5} w="full">
            <Card shadow="sm" _hover={{ shadow: 'md' }} transition="all 0.2s" cursor="pointer"
              onClick={() => navigate('/portal/obras/consulta')}>
              <CardBody p={5}>
                <HStack spacing={3} mb={3}>
                  <Box bg="teal.100" borderRadius="full" p={2}>
                    <Icon as={MdSearch} color="teal.500" boxSize={6} />
                  </Box>
                  <Text fontWeight="bold" color="gray.700">Consultar Estatus</Text>
                </HStack>
                <Text fontSize="sm" color="gray.500" mb={3}>
                  Consulte el avance de su trámite con su folio único (ej. OP-TOT-2026-A7F92K).
                </Text>
                <Button colorScheme="teal" variant="outline" size="sm" w="full">
                  Consultar →
                </Button>
              </CardBody>
            </Card>

            <Card shadow="sm" _hover={{ shadow: 'md' }} transition="all 0.2s" cursor="pointer"
              onClick={() => navigate('/portal/obras/consulta#validar')}>
              <CardBody p={5}>
                <HStack spacing={3} mb={3}>
                  <Box bg="green.100" borderRadius="full" p={2}>
                    <Icon as={MdVerifiedUser} color="green.500" boxSize={6} />
                  </Box>
                  <Text fontWeight="bold" color="gray.700">Validar Documento</Text>
                </HStack>
                <Text fontSize="sm" color="gray.500" mb={3}>
                  Verifique la autenticidad de un documento oficial escaneando su código QR o ingresando el folio de validación.
                </Text>
                <Button colorScheme="green" variant="outline" size="sm" w="full">
                  Validar →
                </Button>
              </CardBody>
            </Card>
          </SimpleGrid>

          {/* Información de contacto */}
          <Card w="full" bg="gray.100" shadow="none">
            <CardBody>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} textAlign="center">
                <VStack spacing={1}>
                  <Icon as={MdAccessTime} color="#5b182b" boxSize={6} />
                  <Text fontWeight="bold" fontSize="sm">Horario de Atención</Text>
                  <Text fontSize="xs" color="gray.600">Lunes a Viernes</Text>
                  <Text fontSize="xs" color="gray.600">8:00 – 15:00 hrs</Text>
                </VStack>
                <VStack spacing={1}>
                  <Icon as={MdPlace} color="#5b182b" boxSize={6} />
                  <Text fontWeight="bold" fontSize="sm">Ubicación</Text>
                  <Text fontSize="xs" color="gray.600">Palacio Municipal</Text>
                  <Text fontSize="xs" color="gray.600">Totolac, Tlaxcala</Text>
                </VStack>
                <VStack spacing={1}>
                  <Icon as={MdAssignment} color="#5b182b" boxSize={6} />
                  <Text fontWeight="bold" fontSize="sm">Área</Text>
                  <Text fontSize="xs" color="gray.600">Dirección de Obras Públicas</Text>
                  <Text fontSize="xs" color="gray.600">y Desarrollo Urbano</Text>
                </VStack>
              </SimpleGrid>
            </CardBody>
          </Card>

          <Text fontSize="xs" color="gray.400" textAlign="center">
            Para aclaraciones, acuda a las oficinas del Ayuntamiento o llame al teléfono oficial.
          </Text>
        </VStack>
      </Box>
    </Box>
  );
};

export default PortalObrasHome;
